import React from 'react';
import './PrintDocument.css';
import { numberToWords } from 'helpers/NumberToWordHelper';
import ShopsHelper from 'helpers/ShopsHelper';
import { COUNTRY_CONFIGRATION, PRINT_TYPES } from 'const';
import { getUpiPaymentDetails } from 'helpers/SaleHelper';
import { QRCodeSVG } from 'qrcode.react';

const A4Invoice = ({ data, type = PRINT_TYPES.SALE, shop, billFormat, totals, terminal }) => {

    if (!data || !shop) return null;

    const items = data.items || [];
    const dateValue = data.time || data.created_at;
    const date = dateValue ? new Date(dateValue) : new Date();
    const options = dateValue ? { timeZone: 'UTC' } : {};
    
    const formattedDate = date.toLocaleDateString('en-GB', options);
    const formattedTime = date.toLocaleTimeString('en-GB', { ...options, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toUpperCase();

    const customer = data.customer || null;
    const shopName = billFormat?.shop_name || shop?.shop_name;

    const formatBalance = (balance) => {
        return balance >= 0 ? ShopsHelper.getAmountFormatted(shop, balance) : `(cr) ${ShopsHelper.getAmountFormatted(shop, Math.abs(balance))}`;
    };

    const payments = data.payments || [];
    const totalPaid = payments.reduce((acc, curr) => acc + Number(curr.credit || 0), 0);
    const creditUsed = (totals?.payable || 0) - totalPaid;
    const prevBalance = Number(customer?.balance || 0) + creditUsed;

    const documentTitle = type === PRINT_TYPES.ESTIMATE ? 'ESTIMATE' : (billFormat?.header || 'TAX INVOICE');
    const documentNumberLabel = type === PRINT_TYPES.ESTIMATE ? 'Estimate No' : 'Invoice No';
    const documentNumber = data.bill_no || data.estimate_no;

    const totalTaxVal = (totals?.tax || 0) + (totals?.cess || 0);
    const discountAppliedOnItems = (data.discount_applied_on_items || 0) + (data.discount || 0);
    const totalAmountVal = (totals?.payable || 0) - totalTaxVal + discountAppliedOnItems;
    const bankData = billFormat?.note || '-';
    const { upiId, upiPaymentUrl } = getUpiPaymentDetails({ terminal, amount: totals?.payable, description: data.bill_no || data.estimate_no, shop });

    return (
        <div className={"print-document-wrapper print-only"}>

            <style type="text/css" media="print">
                {`@page { size: A4 portrait; margin: 10mm 0mm !important; }`}
            </style>

            <div className={`print-document-container size-a4`}>
                <div className="print-header">
                    {(shop?.shop_logo || billFormat?.shop_logo) && (
                        <div className="logo-container">
                            <img src={shop?.shop_logo || billFormat?.shop_logo} alt="Logo" className="shop-logo" />
                        </div>
                    )}
                    <div className={(shop?.shop_logo || billFormat?.shop_logo) ? "shop-details-right" : "shop-details-center"}>
                        <h1 className="shop-name">{shopName}</h1>
                        <div className="shop-details">
                            {(billFormat?.address_1 || shop?.address || shop?.location) && <div>{billFormat?.address_1 || shop?.address || shop?.location}</div>}
                            {(billFormat?.address_2) && <div>{billFormat?.address_2}</div>}
                            {(billFormat?.address_3) && <div>{billFormat?.address_3}</div>}
                            {(billFormat?.address_4) && <div>{billFormat?.address_4}</div>}
                            {(billFormat?.tel_no || shop?.mobile_no || shop?.phone) && <div>Phone: {billFormat?.tel_no || shop?.mobile_no || shop?.phone}</div>}
                            {(billFormat?.tin || shop?.trn_number) && <div>GSTIN: {billFormat?.tin || shop?.trn_number}</div>}
                        </div>
                    </div>
                </div>

                <div className="thick-divider"></div>
                <div className="title-section" >
                    <h2 className="document-title">{documentTitle}</h2>
                </div>
                <div className="thick-divider"></div>

                <div className="info-box">
                    <div className="info-col-left">
                        <div className="info-label-cell">Customer :</div>
                        <div className="customer-details">
                            {customer?.name && <div>{customer.name}</div>}
                            {customer?.mobile && <div>{customer.mobile}</div>}
                            {customer?.location && <div>{customer.location}</div>}
                            {customer?.trn_number && <div>GSTIN: {customer.trn_number}</div>}
                        </div>
                    </div>
                    <div className="info-col-right">
                        <div className="info-row">
                            <strong>{documentNumberLabel} :</strong>
                            <span>{documentNumber}</span>
                        </div>
                        <div className="info-row">
                            <strong>Date :</strong>
                            <span>{formattedDate}</span>
                        </div>
                        {data.shift_id && (
                            <div className="info-row">
                                <strong>Shift ID :</strong>
                                <span>{data.shift_id}</span>
                            </div>
                        )}
                        <div className="info-row">
                            <strong>Time :</strong>
                            <span>{formattedTime}</span>
                        </div>
                    </div>
                </div>

                <div className="invoice-body">
                    <table className="items-table">
                        <thead>
                            <tr>
                                <th className="col-index">SN</th>
                                <th className="col-desc">Item Description</th>
                                <th className="col-hsn">HSN</th>
                                <th className="col-qty">Qty</th>
                                <th className="col-rate">Rate</th>
                                <th className="col-tax">Tax</th>
                                <th className="col-tax-percent">Tax%</th>
                                <th className="col-total">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => {
                                const actualIndex = idx + 1;
                                const itemPrice = item.rate || 0;
                                const itemTax = item.tax || 0;
                                const itemTotal = item.payable || 0;

                                return (
                                    <tr key={idx}>
                                        <td className="text-center">{actualIndex}</td>
                                        <td>
                                            <div className="item-name">{item.name || item.item?.name || item.description}</div>
                                            {(item.name || item.item?.name) && item.description && <div className="item-description">{item.description}</div>}
                                        </td>
                                        <td className="text-center">{item.hsn || item.item?.hsn || '0'}</td>
                                        <td className="text-center">{ShopsHelper.getAmountFormatted(shop, item.quantity)}</td>
                                        <td className="text-right">{ShopsHelper.getAmountFormatted(shop, parseFloat(itemPrice))}</td>
                                        <td className="text-right">{ShopsHelper.getAmountFormatted(shop, parseFloat(itemTax))}</td>
                                        <td className="text-center">{Number(item.item?.tax || 0)}%</td>
                                        <td className="text-right">{ShopsHelper.getAmountFormatted(shop, parseFloat(itemTotal))}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className="footer-filler"></div>

                    <div className="totals-signature-group">
                        <table className="summary-table no-page-break">
                            <tbody>
                                <tr>
                                    <td className="summary-label-cell">Sub Total</td>
                                    <td className="summary-value-cell">{ShopsHelper.getAmountFormatted(shop, totalAmountVal)}</td>
                                </tr>

                                {discountAppliedOnItems > 0 && (
                                    <tr>
                                        <td className="summary-label-cell">Discount</td>
                                        <td className="summary-value-cell">{ShopsHelper.getAmountFormatted(shop, discountAppliedOnItems)}</td>
                                    </tr>
                                )}

                                {totalTaxVal > 0 && (
                                    <>
                                        {shop?.tax_region === COUNTRY_CONFIGRATION.IN.name ? (
                                            <>
                                                <tr>
                                                    <td className="summary-label-cell">CGST</td>
                                                    <td className="summary-value-cell">{ShopsHelper.getAmountFormatted(shop, totalTaxVal / 2)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="summary-label-cell">SGST</td>
                                                    <td className="summary-value-cell">{ShopsHelper.getAmountFormatted(shop, totalTaxVal / 2)}</td>
                                                </tr>
                                            </>
                                        ) : (
                                            <tr>
                                                <td className="summary-label-cell">Total Tax</td>
                                                <td className="summary-value-cell">{ShopsHelper.getAmountFormatted(shop, totalTaxVal)}</td>
                                            </tr>
                                        )}
                                    </>
                                )}

                                <tr>
                                    <td className="summary-label-cell" style={{ fontWeight: 'bold' }}>Payable:</td>
                                    <td className="summary-value-cell" style={{ fontWeight: 'bold' }}>{ShopsHelper.getAmountFormatted(shop, totals?.payable || 0)}</td>
                                </tr>

                                {type !== PRINT_TYPES.ESTIMATE && customer && (creditUsed > 0) && (
                                    <>
                                        <tr>
                                            <td className="summary-label-cell">Prev Balance</td>
                                            <td className="summary-value-cell">{formatBalance(prevBalance)}</td>
                                        </tr>
                                        <tr>
                                            <td className="summary-label-cell">Customer Balance</td>
                                            <td className="summary-value-cell">{formatBalance(Number(customer.balance || 0))}</td>
                                        </tr>
                                    </>
                                )}

                                <tr>
                                    <td colSpan="2" className="summary-text">
                                        Amount in words: <strong>{numberToWords(totals?.payable || 0)}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className={`footer-signature-box ${upiPaymentUrl ? 'has-qr' : ''}`}>
                            <div className="bank-details-col">
                                {bankData || '-'}
                                {data.remark && (
                                    <div className="text-left" style={{ paddingTop: '10px' }}>
                                        <strong>Remark:</strong> {data.remark}
                                    </div>
                                )}
                                <div className="footer-bottom">
                                    {billFormat?.footer_1 && <div>{billFormat.footer_1}</div>}
                                    {billFormat?.footer_2 && <div>{billFormat.footer_2}</div>}
                                </div>
                            </div>
                            {upiPaymentUrl && (
                                <div className="qr-code-col">
                                    <QRCodeSVG value={upiPaymentUrl} size={100} includeMargin />
                                    <div style={{ marginTop: '5px', fontSize: '11px' }}>Scan & Pay</div>
                                    <div style={{ fontSize: '10px' }}>{upiId}</div>
                                </div>
                            )}

                            <div className="signature-col">
                                <div className="signature-label">Authorized Signature</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default A4Invoice;
